// src/cockpit4/components/AccountSettings.tsx
// ============================================
// 账户设置组件 - 修改密码 / 员工管理 / API 余额
//
// 功能：
//   1. 修改自己的密码（所有用户可用，密码规则：大小写+数字+8位以上）
//   2. 员工管理（仅超级管理员：添加/删除员工、修改员工密码）
//   3. API 余额查询（仅超级管理员：DeepSeek 等API余额）
// ============================================

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  KeyRound,
  Users,
  UserPlus,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Wallet,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { useUserStore, validatePassword, type EmployeeRecord } from "../../stores/userStore";
import { apiConfig } from "../../services/apiConfig";

// ============================================
// 主组件
// ============================================

export default function AccountSettings() {
  const { currentUser } = useUserStore();
  const isAdmin = currentUser?.role === "super_admin";

  return (
    <div className="space-y-4">
      {/* 修改密码 */}
      <ChangePasswordSection />

      {/* 超级管理员专属：员工管理 */}
      {isAdmin && <EmployeeManagementSection />}

      {/* 超级管理员专属：API 余额 */}
      {isAdmin && <ApiBalanceSection />}
    </div>
  );
}

// ============================================
// 1. 修改密码面板
// ============================================

function ChangePasswordSection() {
  const { changePassword, currentUser } = useUserStore();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setMessage(null);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "请填写所有字段" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "两次输入的新密码不一致" });
      return;
    }

    // 验证新密码强度
    const validation = validatePassword(newPassword);
    if (!validation.valid) {
      setMessage({ type: "error", text: validation.error || "密码不符合要求" });
      return;
    }

    setLoading(true);
    await new Promise((r) => setTimeout(r, 300));
    const result = await changePassword(oldPassword, newPassword);
    setLoading(false);

    if (result.success) {
      setMessage({ type: "success", text: "密码修改成功，下次登录请使用新密码" });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } else {
      setMessage({ type: "error", text: result.error || "修改失败" });
    }
  };

  // 密码强度指示器
  const strength = getPasswordStrength(newPassword);

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
      {/* 标题 */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
          <KeyRound size={13} className="text-amber-400" />
        </div>
        <div>
          <h3 className="text-[13px] font-semibold text-white">修改密码</h3>
          <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
            {currentUser?.displayName} · 密码需包含大小写字母和数字，至少8位
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {/* 原密码 */}
        <PasswordInput
          label="原密码"
          value={oldPassword}
          onChange={setOldPassword}
          show={showOld}
          onToggle={() => setShowOld(!showOld)}
          placeholder="输入当前密码"
        />

        {/* 新密码 */}
        <PasswordInput
          label="新密码"
          value={newPassword}
          onChange={setNewPassword}
          show={showNew}
          onToggle={() => setShowNew(!showNew)}
          placeholder="输入新密码（大小写字母+数字，至少8位）"
        />

        {/* 密码强度指示器 */}
        {newPassword && (
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-mono text-gray-500">强度:</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((level) => (
                <div
                  key={level}
                  className="h-1 w-8 rounded-full transition-colors"
                  style={{
                    background: level <= strength.score ? strength.color : "rgba(255,255,255,0.08)",
                  }}
                />
              ))}
            </div>
            <span className="text-[9px] font-mono" style={{ color: strength.color }}>
              {strength.label}
            </span>
          </div>
        )}

        {/* 确认新密码 */}
        <PasswordInput
          label="确认新密码"
          value={confirmPassword}
          onChange={setConfirmPassword}
          show={showConfirm}
          onToggle={() => setShowConfirm(!showConfirm)}
          placeholder="再次输入新密码"
        />

        {/* 消息提示 */}
        {message && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className={`flex items-center gap-2 px-3 py-2 rounded-md ${
              message.type === "success"
                ? "bg-emerald-500/10 border border-emerald-500/20"
                : "bg-red-500/10 border border-red-500/20"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
            ) : (
              <AlertCircle size={13} className="text-red-400 flex-shrink-0" />
            )}
            <span className={`text-[11px] ${message.type === "success" ? "text-emerald-300" : "text-red-300"}`}>
              {message.text}
            </span>
          </motion.div>
        )}

        {/* 提交按钮 */}
        <button
          onClick={handleSubmit}
          disabled={loading || !oldPassword || !newPassword || !confirmPassword}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-medium hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
          确认修改
        </button>
      </div>
    </div>
  );
}

// ============================================
// 2. 员工管理面板（仅超级管理员）
// ============================================

function EmployeeManagementSection() {
  const { listEmployees, addEmployee, removeEmployee, changeEmployeePassword } = useUserStore();
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);

  // 添加员工表单
  const [newUsername, setNewUsername] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [addMessage, setAddMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // 修改员工密码
  const [empNewPassword, setEmpNewPassword] = useState("");
  const [empPwdMessage, setEmpPwdMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const refresh = useCallback(() => {
    setEmployees(listEmployees());
  }, [listEmployees]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = async () => {
    setAddMessage(null);
    if (!newUsername || !newDisplayName || !newPhone || !newPassword) {
      setAddMessage({ type: "error", text: "请填写所有字段" });
      return;
    }
    const validation = validatePassword(newPassword);
    if (!validation.valid) {
      setAddMessage({ type: "error", text: validation.error || "密码不符合要求" });
      return;
    }
    const result = addEmployee({
      username: newUsername,
      displayName: newDisplayName,
      phone: newPhone,
      password: newPassword,
    });
    if (result.success) {
      setAddMessage({ type: "success", text: `员工 ${newUsername} 添加成功` });
      setNewUsername("");
      setNewDisplayName("");
      setNewPhone("");
      setNewPassword("");
      refresh();
    } else {
      setAddMessage({ type: "error", text: result.error || "添加失败" });
    }
  };

  const handleDelete = (username: string) => {
    if (!confirm(`确认删除员工 ${username}？此操作不可撤销。`)) return;
    const result = removeEmployee(username);
    if (result.success) {
      refresh();
    } else {
      alert(result.error || "删除失败");
    }
  };

  const handleChangeEmpPassword = () => {
    setEmpPwdMessage(null);
    if (!editingUser || !empNewPassword) {
      setEmpPwdMessage({ type: "error", text: "请输入新密码" });
      return;
    }
    const validation = validatePassword(empNewPassword);
    if (!validation.valid) {
      setEmpPwdMessage({ type: "error", text: validation.error || "密码不符合要求" });
      return;
    }
    const result = changeEmployeePassword(editingUser, empNewPassword);
    if (result.success) {
      setEmpPwdMessage({ type: "success", text: `员工 ${editingUser} 密码已修改` });
      setEmpNewPassword("");
      setTimeout(() => {
        setEditingUser(null);
        setEmpPwdMessage(null);
      }, 2000);
    } else {
      setEmpPwdMessage({ type: "error", text: result.error || "修改失败" });
    }
  };

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
      {/* 标题 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <Users size={13} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">员工管理</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              共 {employees.length} 名员工 · 超级管理员专属
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[10px] font-medium hover:bg-blue-500/25 transition-all"
        >
          <UserPlus size={11} />
          {showAddForm ? "取消" : "添加员工"}
        </button>
      </div>

      {/* 添加员工表单 */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mb-4 p-3 rounded-md border border-blue-500/20 bg-blue-500/[0.02] space-y-2"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="登录用户名"
              className="px-3 py-2 rounded-md bg-black/30 border border-white/[0.08] text-white text-[11px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50"
            />
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder="显示名称"
              className="px-3 py-2 rounded-md bg-black/30 border border-white/[0.08] text-white text-[11px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50"
            />
            <input
              type="text"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="手机号"
              className="px-3 py-2 rounded-md bg-black/30 border border-white/[0.08] text-white text-[11px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50"
            />
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="初始密码（大小写+数字，8位+）"
              className="px-3 py-2 rounded-md bg-black/30 border border-white/[0.08] text-white text-[11px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50"
            />
          </div>
          {addMessage && (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] ${
              addMessage.type === "success" ? "text-emerald-300" : "text-red-300"
            }`}>
              {addMessage.type === "success" ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
              {addMessage.text}
            </div>
          )}
          <button
            onClick={handleAdd}
            className="px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/30 text-blue-300 text-[10px] font-medium hover:bg-blue-500/30 transition-all"
          >
            确认添加
          </button>
        </motion.div>
      )}

      {/* 员工列表 */}
      <div className="space-y-1.5">
        {employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-gray-600">
            <Users size={20} className="mb-2 opacity-50" />
            <span className="text-[11px]">暂无员工，点击"添加员工"创建</span>
          </div>
        ) : (
          employees.map((emp) => (
            <div
              key={emp.username}
              className="flex items-center justify-between py-2 px-3 rounded-md border border-white/[0.04] bg-white/[0.01]"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                  <span className="text-[10px] font-mono text-blue-300">
                    {emp.displayName.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-[12px] text-white font-medium">{emp.displayName}</div>
                  <div className="text-[9px] font-mono text-gray-500">
                    @{emp.username} · {emp.phone}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setEditingUser(editingUser === emp.username ? null : emp.username);
                    setEmpPwdMessage(null);
                  }}
                  className="p-1.5 rounded text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                  title="修改密码"
                >
                  <KeyRound size={12} />
                </button>
                <button
                  onClick={() => handleDelete(emp.username)}
                  className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="删除员工"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 修改员工密码面板 */}
      {editingUser && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-3 p-3 rounded-md border border-amber-500/20 bg-amber-500/[0.02] space-y-2"
        >
          <div className="text-[11px] text-amber-300 font-medium">
            修改员工 @{editingUser} 的密码
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={empNewPassword}
              onChange={(e) => setEmpNewPassword(e.target.value)}
              placeholder="输入新密码（大小写+数字，8位+）"
              className="flex-1 px-3 py-2 rounded-md bg-black/30 border border-white/[0.08] text-white text-[11px] placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50"
            />
            <button
              onClick={handleChangeEmpPassword}
              className="px-3 py-2 rounded-md bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[10px] font-medium hover:bg-amber-500/30 transition-all"
            >
              确认
            </button>
          </div>
          {empPwdMessage && (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] ${
              empPwdMessage.type === "success" ? "text-emerald-300" : "text-red-300"
            }`}>
              {empPwdMessage.type === "success" ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
              {empPwdMessage.text}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

// ============================================
// 3. API 余额面板（仅超级管理员）
// ============================================

interface BalanceInfo {
  id: string;
  name: string;
  available: boolean;
  balance?: string;
  currency?: string;
  granted?: string;
  toppedUp?: string;
  error?: string;
  loading: boolean;
}

function ApiBalanceSection() {
  const [balances, setBalances] = useState<BalanceInfo[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchBalances = useCallback(async () => {
    setRefreshing(true);
    const configs = apiConfig.getAll();
    const results: BalanceInfo[] = [];

    for (const config of configs) {
      if (!config.enabled || !config.apiKey) {
        results.push({
          id: config.id,
          name: config.name,
          available: false,
          loading: false,
          error: "未配置 API Key",
        });
        continue;
      }

      const info: BalanceInfo = {
        id: config.id,
        name: config.name,
        available: false,
        loading: true,
      };

      try {
        // DeepSeek 余额查询（DeepSeek 官方 API: GET /user/balance）
        if (config.id === "deepseek") {
          const resp = await fetch("/deepseek-api/user/balance", {
            headers: {
              "Authorization": `Bearer ${config.apiKey}`,
            },
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.is_available !== undefined) {
              info.available = data.is_available;
              if (data.balance_info) {
                info.balance = data.balance_info.total_balance;
                info.currency = data.balance_info.currency;
                info.granted = data.balance_info.granted_balance;
                info.toppedUp = data.balance_info.topped_up_balance;
              }
            }
          } else {
            info.error = `HTTP ${resp.status}`;
          }
        } else if (config.id === "duomi") {
          // 多米 API 暂无余额查询接口
          info.available = true;
          info.balance = "—";
          info.error = "多米 API 暂不支持余额查询";
        } else {
          info.available = true;
          info.balance = "—";
          info.error = "该 API 暂不支持余额查询";
        }
      } catch (err) {
        info.error = err instanceof Error ? err.message : String(err);
      }

      info.loading = false;
      results.push(info);
    }

    setBalances(results);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
      {/* 标题 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Wallet size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">API 余额</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              各 API 平台余额与消耗状态 · 超级管理员专属
            </p>
          </div>
        </div>
        <button
          onClick={fetchBalances}
          disabled={refreshing}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 hover:text-gray-200 transition-colors"
        >
          <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
          刷新
        </button>
      </div>

      {/* 余额列表 */}
      <div className="space-y-2">
        {balances.length === 0 && refreshing ? (
          <div className="flex items-center justify-center py-6 text-gray-500">
            <Loader2 size={14} className="animate-spin mr-2" />
            <span className="text-[11px]">查询中...</span>
          </div>
        ) : (
          balances.map((bal) => (
            <div
              key={bal.id}
              className="flex items-center justify-between py-2.5 px-3 rounded-md border border-white/[0.04] bg-white/[0.01]"
            >
              <div className="flex items-center gap-2.5">
                {bal.loading ? (
                  <Loader2 size={13} className="text-gray-400 animate-spin" />
                ) : bal.available ? (
                  <CheckCircle2 size={13} className="text-emerald-400" />
                ) : (
                  <XCircle size={13} className="text-red-400" />
                )}
                <div>
                  <div className="text-[12px] text-white font-medium">{bal.name}</div>
                  <div className="text-[9px] font-mono text-gray-500">
                    {bal.error || (bal.available ? "可用" : "不可用")}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {bal.balance && bal.balance !== "—" ? (
                  <>
                    <div className="text-[14px] font-mono font-bold text-emerald-300">
                      {bal.balance}
                      <span className="text-[9px] text-gray-500 ml-1">{bal.currency || "CNY"}</span>
                    </div>
                    {bal.granted && bal.toppedUp && (
                      <div className="text-[9px] font-mono text-gray-600">
                        赠送 {bal.granted} / 充值 {bal.toppedUp}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-[11px] font-mono text-gray-600">—</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================
// 辅助组件：密码输入框
// ============================================

interface PasswordInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggle: () => void;
  placeholder?: string;
}

function PasswordInput({ label, value, onChange, show, onToggle, placeholder }: PasswordInputProps) {
  return (
    <div>
      <label className="block text-[10px] font-mono text-gray-400 mb-1.5 uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <Lock size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 rounded-md bg-black/30 border border-white/[0.08] text-white text-[11px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
        >
          {show ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
      </div>
    </div>
  );
}

// ============================================
// 辅助函数：密码强度计算
// ============================================

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: "", color: "#6B7280" };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: "弱", color: "#EF4444" };
  if (score <= 2) return { score: 2, label: "中", color: "#F59E0B" };
  if (score <= 3) return { score: 3, label: "强", color: "#3B82F6" };
  return { score: 4, label: "极强", color: "#10B981" };
}
